import React, { useEffect, useState } from 'react'

import { Card } from 'components'
import { SuccessModal } from 'components/SuccessModal/SuccessModal'
import { useLoading, useToggle, useModal, usePersistForm } from 'hooks'
import {
  SupplierRegistrationServices,
  IFornecedor
} from 'services/NGT/SupplierRegistration'
import { COLORS, FORMAT, SPACING } from 'utils'

import {
  FilterTableSupplier,
  TableSearchSupplier,
  SupplierRecordModal
} from './components'
import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

const SCHEMA = yup.object({
  CdDocto: yup.string().nullable().notRequired(),
  CdFornecedorSap: yup.string().nullable().notRequired(),
  CdTipoFornecedor: yup.string(),
  NmFornecedor: yup.string().nullable().notRequired(),
  CdFornecedor: yup.string().nullable().notRequired(),
})

export interface SearchSupplierRegistrationProps {
  CdDocto: string
  selectedPersonType: string
  CdFornecedorSap: string
  CdTipoFornecedor: string
  NmFornecedor: string
  CdFornecedor: string
}

const SearchSupplierRegistration: React.FC = () => {
  const { loading, msgErro, setLoading, setMsgErro } = useLoading()
  const { showModal } = useModal()
  const [supplierTableData, setSupplierTableData] = useState<any[]>([])
  const [selectedSupplier, setSelectedSupplier] = useState<
    IFornecedor | undefined
  >()
  const [openSupplierRecordModal, toggleSupplierRecordModal] = useToggle()
  const [page, setPage] = useState<number>(1)
  const [rowsPerPage, setRowsPerPage] = useState<number>(15)
  const [totalItems, setTotalItems] = useState<number>(0)

  const { control, watch, reset, handleSubmit, setValue } = useForm<SearchSupplierRegistrationProps>({
    resolver: yupResolver(SCHEMA),
    defaultValues: {
      CdTipoFornecedor: 'T',
    }
  })

  usePersistForm('ngt_search_supplier_registration', {
    setValue,
    watch
  })

  const supplierType = watch('CdTipoFornecedor')

  const handleClearFilters = () => {
    setPage(1)
    reset()
    handleSearch(watch())
  }

  const handleSearch = async (form: SearchSupplierRegistrationProps = {} as SearchSupplierRegistrationProps) => {
    setLoading('loading')

    const requestQUERY: any = {
      CdDocto:
        form.CdTipoFornecedor === 'E'
          ? form.CdDocto
          : FORMAT.unMask(form.CdDocto),
      CdFornecedor: parseInt(form.CdFornecedor) > 0 ? Number(form.CdFornecedor) : null,
      CdFornecedorSap: form.CdFornecedorSap ?? '',
      CdTipoFornecedor: form.CdTipoFornecedor === "T" ? null : form.CdTipoFornecedor,
      NmFornecedor: form.NmFornecedor ? form.NmFornecedor : null,
      PageNumber: page,
      PageSize: rowsPerPage
    }

    SupplierRegistrationServices().getFilterProps(
      requestQUERY
    ).then((response) => {
      if (response.status !== 200) throw new Error(response.statusText)
      setPage(page > response.data.pageCount ? 1 : page)
      setTotalItems(response.data.pageCount)
      setSupplierTableData(response.data.items)
      setLoading('idle')
    }).catch(() => {
      setLoading('idle')
      showModal({
        message: 'Não foi possível carregar lista de Fornecedores, tente novamente!',
        titleModal: 'Encontramos um problema.',
        type: 'alert',
        hideButtonCancel: true,
        hideThirdButton: true,
      })
    })
  }

  useEffect(() => {
    handleSearch(watch())
  }, [rowsPerPage, page])

  const handleSelectedSupplier = (currentSupplier: IFornecedor) => {
    setLoading('loading')
    SupplierRegistrationServices()
      .get(currentSupplier.idFornecedor)
      .then(({ data }) => {
        setSelectedSupplier(data)
        setLoading('idle')

        toggleSupplierRecordModal()
      })
      .catch((err) => {
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message: 'Não foi possível carregar os dados do Fornecedor, tente novamente!',
          txtButtonConfirm: 'Fechar',
          type: 'error'
        })
      })
  }

  const limparContatosVazios = (requestData) => {
    requestData.tFornecedorContatos.forEach((contact) => {
      if (contact.nmContato == '' || contact.txEmail == '') {
        requestData.tFornecedorContatos =
          requestData.tFornecedorContatos.filter(
            (contact) => contact.nmContato && contact.txEmail
          )
      }
    })
  }

  const handleEditSupplier = (requestData: IFornecedor) => {
    setLoading('loading')

    requestData.nuCelResponsavel
    requestData.nuCelular
    requestData.nuTelefone
    const requestDataObj: IFornecedor = {
      ...requestData,
      nuTelefone: FORMAT.unMask(requestData.nuTelefone).trim(),
      nuCelular: FORMAT.unMask(requestData.nuCelResponsavel).trim(),
      nuCelResponsavel: FORMAT.unMask(requestData.nuCelResponsavel).trim(),
      cdCep: FORMAT.unMask(requestData.cdCep),
      cdCpfResponsavel: isNaN(parseInt(requestData.cdCpfResponsavel))
        ? requestData.cdCpfResponsavel
        : FORMAT.unMask(requestData.cdCpfResponsavel),
      cdDocto:
        requestData.cdTipoFornecedor === 'E'
          ? requestData.cdDocto
          : FORMAT.unMask(requestData.cdDocto)
    }

    limparContatosVazios(requestDataObj)

    delete requestDataObj.contatosComercial
    delete requestDataObj.contatosTrabalhista
    delete requestDataObj.contatosSeguranca
    delete requestDataObj.contatosImportacao

    SupplierRegistrationServices()
      .patch(requestDataObj)
      .then((response) => {
        if (response.status !== 200 || !response.data.sucesso) {
          throw new Error(
            response.data.mensagem || 'Algo deu errado, tente novamente.'
          )
        }

        setLoading('idle')
        toggleSupplierRecordModal()

        showModal({
          hideButtonCancel: true,
          message: 'Dados alterados com sucesso',
          txtButtonConfirm: 'Fechar',
          type: 'success'
        })
      })
      .catch((error) => {
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message: 'Não foi possivel obter o dados da pesquisa',
          txtButtonConfirm: 'Fechar',
          type: 'error'
        })
      })
  }

  return (
    <>
      <Card
        marginLeft={SPACING.spacing20}
        marginRight={SPACING.spacing20}
        marginBottom={SPACING.spacing100}
        padding={SPACING.spacing20}
        boxShadow={`0px -1px 32px rgba(50, 50, 71, 0.12), 0px 24px 32px rgba(50, 50, 71, 0.12)`}
        backgroundColor={COLORS.white}
        borderRadius={`0px 10px 0px 0px`}
      >
        <>
          <FilterTableSupplier
            control={control}
            handleSubmit={handleSubmit}
            handleSearchSupplier={handleSearch}
            handleClearFilters={handleClearFilters}
            supplierType={supplierType}
          />
          <TableSearchSupplier
            tableData={supplierTableData}
            selectedSupplier={handleSelectedSupplier}
            totalItems={totalItems}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
          />
        </>
      </Card>
      <SupplierRecordModal
        showModal={openSupplierRecordModal}
        toggleModal={toggleSupplierRecordModal}
        supplierData={selectedSupplier}
        handleSubmitUpdate={handleEditSupplier}
      />
      <SuccessModal
        open={loading === 'error'}
        message={msgErro}
        type="error"
        hideButtonCancel
        handleConfirm={() => setLoading('idle')}
        txtButtonConfirm="Voltar"
      />
    </>
  )
}

export default SearchSupplierRegistration
